import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  User as UserIcon,
  Upload,
  FileText,
  Users,
  Trophy,
  BarChart3,
  Menu,
  X,
  Brain,
  Briefcase,
  CheckCircle,
  XCircle,
  BookOpen,
  Crown,
  Database,
} from "lucide-react";

interface SidebarProps {
  userType: "jobseeker" | "hr" | "admin";
}

interface NavigationItem {
  path: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  label: string;
  badge?: string;
}

const SIDEBAR_STATE_KEY = "vetria.ui.sidebar.expanded";

const cn = (
  ...classes: Array<string | false | null | undefined>
): string => classes.filter(Boolean).join(" ");

const Sidebar: React.FC<SidebarProps> = ({ userType }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(SIDEBAR_STATE_KEY);
    return stored ? JSON.parse(stored) : true;
  });
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(isExpanded));
  }, [isExpanded]);

  const navigationItems: NavigationItem[] = useMemo(() => {
    switch (userType) {
      case "jobseeker":
        return [
          { path: "/job-seeker/profile", icon: UserIcon, label: "Profile" },
          { path: "/job-seeker/resume-upload", icon: Upload, label: "Resume Upload" },
          { path: "/job-seeker/tests", icon: FileText, label: "Available Jobs" },
          { path: "/job-seeker/my-courses", icon: BookOpen, label: "My Courses" },
          { path: "/job-seeker/results", icon: Trophy, label: "Recent Results" },
          { path: "/job-seeker/subscription", icon: Crown, label: "Go Premium", badge: "Pro" },
        ];
      case "hr":
        return [
          { path: "/hr/profile", icon: UserIcon, label: "Profile" },
          { path: "/hr/jd-upload", icon: Upload, label: "Upload JD" },
          { path: "/hr/my-jobs", icon: Briefcase, label: "Jobs" },
          { path: "/hr/test-generator", icon: Brain, label: "AI Test Generator", badge: "New" },
          { path: "/hr/my-tests", icon: FileText, label: "Tests Library" },
          { path: "/hr/candidates", icon: Users, label: "Candidates" },
          { path: "/hr/shortlisted", icon: CheckCircle, label: "Shortlisted" },
          { path: "/hr/rejected", icon: XCircle, label: "Rejected" },
        ];
      case "admin":
        return [
          { path: "/admin", icon: Home, label: "Dashboard" },
          { path: "/admin/users", icon: Users, label: "User Management" },
          { path: "/admin/badges", icon: Trophy, label: "Badge Management" },
          { path: "/admin/migrate-tests", icon: Database, label: "Migrate Tests", badge: "New" },
          { path: "/admin/analytics", icon: BarChart3, label: "Analytics" },
        ];
      default:
        return [];
    }
  }, [userType]);

  return (
    <aside
      className={cn(
        "relative flex min-h-screen flex-col backdrop-blur-xl transition-[width] duration-300 ease-smooth border-r border-white/30 shadow-2xl",
        isExpanded ? "w-[280px]" : "w-20"
      )}
      style={{
        background: 'linear-gradient(180deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%)'
      }}
    >
      {/* Glassmorphic overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none"></div>

      <div className={cn(
        "relative flex items-center py-6 z-10",
        isExpanded ? "justify-between px-5" : "justify-center px-3"
      )}>
        <Link
          to="/"
          aria-label="VetriaAI"
          className={cn(
            "flex items-center gap-3 transition-all duration-300",
            isExpanded ? "opacity-100" : "opacity-0 pointer-events-none w-0"
          )}
        >
          <div className="h-11 w-11 rounded-xl bg-white/20 backdrop-blur-md p-2.5 shadow-lg border border-white/30">
            <img
              src="/vetriaAI.png"
              alt="VetriaAI"
              className="h-full w-full object-contain"
            />
          </div>
          <span className="text-xl font-bold tracking-tight text-white drop-shadow-lg">
            VetriaAI
          </span>
        </Link>
        <button
          onClick={() => setIsExpanded((previous) => !previous)}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 backdrop-blur-md text-white transition-all duration-200 hover:bg-white/20 hover:scale-110 border border-white/20 shadow-md"
          aria-label={isExpanded ? "Collapse navigation" : "Expand navigation"}
          title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          {isExpanded ? (
            <X className="h-5 w-5" strokeWidth={2.5} />
          ) : (
            <Menu className="h-5 w-5" strokeWidth={2.5} />
          )}
        </button>
      </div>

      <nav className="relative flex-1 space-y-2 px-3 py-6 z-10">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              title={!isExpanded ? item.label : undefined}
              className={cn(
                "group flex items-center gap-4 rounded-xl px-4 py-3.5 text-base font-semibold text-white transition-all duration-200 relative overflow-hidden",
                isActive
                  ? "bg-white/25 backdrop-blur-md shadow-lg border border-white/40"
                  : "hover:bg-white/10 hover:backdrop-blur-md hover:scale-[1.02] border border-transparent"
              )}
            >
              {/* Active indicator */}
              {isActive && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full shadow-glow-blue"></div>
              )}

              <Icon className="h-6 w-6 flex-none drop-shadow-md" strokeWidth={2} />
              {isExpanded && (
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="drop-shadow-sm">{item.label}</span>
                  {item.badge && (
                    <span className="inline-flex items-center rounded-pill px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider shadow-md" style={{
                      background: 'linear-gradient(135deg, #B91C1C 0%, #991B1B 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.3)'
                    }}>
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom gradient decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
    </aside>
  );
};

export default Sidebar;
